import { Constants } from '../../../../utils/constants'
import { EvidencesTypeValueEnum } from '../../../../utils/enums/evidences'
import { AutomaticTempByIdResponse } from '../../../../service/actionPlans/response'
import { Evidence } from '../../../../service/reportFolio/response'
import { QuestionWithEvidence } from '../form/components/questionWithEvidencesSection'
import {
  ActionPlanToGenerateDetail,
  AssignmentMode,
  EvidenceItem,
  QuestionWithEvidences,
} from './types'

const getFullName = (user: { firstName: string; lastName: string }) =>
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()

const mapEvidence = (
  evidence: AutomaticTempByIdResponse['items'][0]['evidences'][0]
): EvidenceItem | null => {
  if (evidence.comments?.length) {
    return {
      type: EvidencesTypeValueEnum.COMMENT,
      src: '',
      name: evidence.comments.join('\n'),
    }
  }

  switch (evidence.evidenceTypeId) {
    case EvidencesTypeValueEnum.IMAGE:
    case EvidencesTypeValueEnum.CAMERA:
      return {
        type: EvidencesTypeValueEnum.IMAGE,
        src: evidence.url ?? '',
        name: evidence.originalname,
      }
    case EvidencesTypeValueEnum.VIDEO:
    case EvidencesTypeValueEnum.VIDEO_GALLERY:
      return {
        type: EvidencesTypeValueEnum.VIDEO,
        src: evidence.url ?? '',
        name: evidence.originalname,
      }
    case EvidencesTypeValueEnum.SIGN:
      return {
        type: EvidencesTypeValueEnum.SIGN,
        src: evidence.url ?? '',
        name: evidence.originalname,
      }
    case EvidencesTypeValueEnum.DOCUMENT:
      return {
        type: EvidencesTypeValueEnum.DOCUMENT,
        src: evidence.url ?? '',
        name: evidence.originalname,
      }
    case EvidencesTypeValueEnum.COMMENT:
    case EvidencesTypeValueEnum.OBSERVATION_COMMENT:
      return {
        type: EvidencesTypeValueEnum.COMMENT,
        src: '',
        name: evidence.url ?? evidence.originalname,
      }
    default:
      return null
  }
}

const groupEvidences = (
  evidences: AutomaticTempByIdResponse['items'][0]['evidences']
) => {
  const groups = {
    image: [] as EvidenceItem[],
    video: [] as EvidenceItem[],
    comment: [] as EvidenceItem[],
    sign: [] as EvidenceItem[],
    documents: [] as EvidenceItem[],
  }

  evidences.forEach((evidence) => {
    if (evidence.comments?.length) {
      evidence.comments.forEach((comment) => {
        groups.comment.push({
          type: EvidencesTypeValueEnum.COMMENT,
          src: '',
          name: comment,
        })
      })
      return
    }

    const mapped = mapEvidence(evidence)
    if (!mapped) return

    if (mapped.type === EvidencesTypeValueEnum.IMAGE) groups.image.push(mapped)
    else if (mapped.type === EvidencesTypeValueEnum.VIDEO) groups.video.push(mapped)
    else if (mapped.type === EvidencesTypeValueEnum.SIGN) groups.sign.push(mapped)
    else if (mapped.type === EvidencesTypeValueEnum.DOCUMENT) groups.documents.push(mapped)
    else groups.comment.push(mapped)
  })

  return groups
}

const getAssignmentMode = (
  response: AutomaticTempByIdResponse
): AssignmentMode => {
  if (response.assignedQuestionnaireId) return 'by-unit'
  if (response.userQuestionnaireId && response.storeId) return 'by-user-with-unit'
  return 'by-user-without-unit'
}

const mapAutomaticTempToDetail = (
  response: AutomaticTempByIdResponse
): ActionPlanToGenerateDetail => {
  const mode = getAssignmentMode(response)
  const isByUserWithUnit = mode === 'by-user-with-unit'

  return {
    id: response.id,
    templateName: response.questionnairName,
    mode,
    assigningUser: {
      id: response.userCreator.id,
      fullName: getFullName(response.userCreator),
      date: response.createTime,
    },
    assignedUser:
      isByUserWithUnit && response.store
        ? {
            id: response.store.userManageId,
            fullName: response.store.userManageName,
            date: response.createTime,
          }
        : undefined,
    approvers: response.approverUsers.map((user) => ({
      id: user.id,
      fullName: getFullName(user),
      email: user.email,
    })),
    store: response.store
      ? {
          name: response.store.name,
          keyCode: response.store.keyCode,
          managerName: response.store.userManageName,
          timeZone: response.store.storeTimeZone?.zone ?? Constants.defaultTimeZoneZone,
        }
      : undefined,
    questionList: response.items.map((item): QuestionWithEvidence => {
      const selectedAnswers = item.question.answers.filter((answer) => answer.selected)

      return {
        question: {
          id: item.answerId,
          title: item.question.title,
          selected: selectedAnswers.length > 0,
        },
        answerText: selectedAnswers.map((answer) => answer.title).join(' / ') || undefined,
        fullAnswer: selectedAnswers[0]?.fullAnswer,
        evidences: item.evidences as Evidence[],
      }
    }),
    questions: response.items.map((item): QuestionWithEvidences => {
      const selectedAnswers = item.question.answers
        .filter((answer) => answer.selected)
        .map((answer) => answer.title)
        .join(' / ')

      return {
        id: item.id,
        question: item.question.title,
        answer: selectedAnswers,
        evidences: groupEvidences(item.evidences),
      }
    }),
  }
}

export { mapAutomaticTempToDetail }
